// Search-tag helpers split from StateProductPage.tsx so route head() code
// does not pull the full page layout into the shared first-load bundle.
import { brokerOrgJsonLd } from "@/lib/seo-schema";
import { fitDescription } from "@/lib/seo-head";
import type { Product } from "@/data/products";
import type { StateInfo } from "@/data/states";
import { investorToolFaqs } from "@/data/investor-tool-faqs";

export function buildStateFaq(product: Product, state: StateInfo) {
  return [
    {
      q: `Do you offer ${product.shortName} loans in ${state.name}?`,
      a: `Yes — Simply Approved Mortgages actively lends ${product.shortName.toLowerCase()} loans across all of ${state.name}, including ${state.topMarkets.join(", ")}. ${state.licensingNote}`,
    },
    {
      q: `What ${product.shortName.toLowerCase()} loan amounts are available in ${state.name}?`,
      a: `Our ${product.shortName.toLowerCase()} program funds ${product.minLoan} to ${product.maxLoan} in ${state.name}. Typical deals size to ${state.name}'s median value near ${state.medianHomeValue}.`,
    },
    {
      q: `What DSCR ratio do I need in ${state.name}?`,
      a: `We fund ${product.shortName.toLowerCase()} deals in ${state.name} at ${product.dscrMin} DSCR. With ${state.name}'s cap-rate band of ${state.capRateBand}, most cash-flow markets clear 1.10+ comfortably.`,
    },
    {
      q: `Can I close in an LLC in ${state.name}?`,
      a: `Yes. All ${product.shortName.toLowerCase()} loans in ${state.name} close in your entity of choice — LLC, LP, S-Corp, C-Corp, or Series LLC — at no rate premium.`,
    },
    {
      q: `How long does a ${state.name} ${product.shortName.toLowerCase()} loan take to close?`,
      a: `Most ${product.shortName.toLowerCase()} loans in ${state.name} close in 15–21 business days from full application. Appraisal turn-times drive the timeline more than underwriting.`,
    },
    {
      q: `Do I need to live in ${state.name} to qualify?`,
      a: `No. ${product.shortName} loans are business-purpose and available to out-of-state investors, non-resident borrowers, and (for our Foreign National program) non-US citizens investing in ${state.name} rental property.`,
    },
    ...product.faq.slice(0, 2),
    ...investorToolFaqs(state.name),
  ];
}

export function stateProductFaqJsonLd(product: Product, state: StateInfo) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: buildStateFaq(product, state).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function stateProductServiceJsonLd(product: Product, state: StateInfo, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    name: `${product.shortName} Loans in ${state.name}`,
    url,
    provider: brokerOrgJsonLd(),
    areaServed: {
      "@type": "State",
      name: state.name,
    },
    feesAndCommissionsSpecification: `Loan amounts ${product.minLoan}–${product.maxLoan}, up to ${product.ltvMax} LTV, ${product.dscrMin} DSCR, ${product.reserves} reserves. ${product.terms}.`,
    description: `${product.description} Available across ${state.name}.`,
  };
}

export function stateProductMetaDescription(product: Product, state: StateInfo): string {
  const top = state.topMarkets.slice(0, 3).join(", ");
  // product.ltvMax / dscrMin are prose on some programs ("Up to 75% purchase &
  // rate/term · 70% cash-out"), which used to overflow the snippet and get cut
  // mid-sentence. Keep the description to values that always render cleanly and
  // let the on-page matrix carry the full guideline detail.
  return fitDescription(
    `${product.shortName} loans in ${state.name} from ${product.minLoan} to ${product.maxLoan}. Business-purpose financing for investors — LLC-friendly, no personal income docs, 15–21 day close. Active in ${top}.`,
  );
}
